import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    IconButton,
    Tooltip,
    TextField,
    InputAdornment,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import {
    Edit as EditIcon,
    QrCode2 as QrCodeIcon,
    Link as LinkIcon,
    ContentCopy as ContentCopyIcon,
    FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import CustomTable from '../../ReusableComponents/Table/CustomTable';
import CustomSnackbar from '../../components/CustomSnackbar';
import SearchBar from '../../ReusableComponents/Search/SearchBar';
import { ButtonStyles } from '../../Styles/ButtonStyles';
import en_ja from '../../translations/en_ja';
import AddDeepLink from './AddDeepLink';
import api from '../../api/api';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';

const DeepLinkManagement = () => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [allDeepLinks, setAllDeepLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [editMode, setEditMode] = useState(false);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarType, setSnackbarType] = useState('success');

    const [selectedDeepLink, setSelectedDeepLink] = useState(null);
    const [originalDeepLinkData, setOriginalDeepLinkData] = useState(null);

    const [qrDialogOpen, setQrDialogOpen] = useState(false);
    const [qrImage, setQrImage] = useState('');
    const [qrLoading, setQrLoading] = useState(false);
    const [qrTokenId, setQrTokenId] = useState('');
    const [qrDeepLinkUrl, setQrDeepLinkUrl] = useState('');

    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deepLinkToDelete, setDeepLinkToDelete] = useState(null);

    const handleEdit = (row) => {
        setSelectedDeepLink(row);
        setOriginalDeepLinkData(row);
        setEditMode(true);
        setDialogOpen(true);
    };

    const handleDialogSubmit = () => {
        getAllDeepLinks();
    };

    const handleDeleteClick = (row) => {
        setDeepLinkToDelete(row);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!deepLinkToDelete) return;

        try {
            await api.delete(`/v1/admin/deep-links/${deepLinkToDelete.id}`);

            setDeleteConfirmOpen(false);
            setDeepLinkToDelete(null);

            setSnackbarMessage(en_ja["Deep link deleted successfully"]);
            setSnackbarType("success");
            setSnackbarOpen(true);

            getAllDeepLinks();
        } catch (error) {
            console.error("Error deleting deep link:", error);

            setSnackbarMessage(en_ja["Failed to delete deep link"]);
            setSnackbarType("error");
            setSnackbarOpen(true);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setDeepLinkToDelete(null);
    };

    const handleToggleStatus = (row) => {
        const obj = {
            status: row.status === 'active'
                ? 'disabled'
                : 'active',
        };

        api.patch(`/v1/admin/deep-links/${row.id}/status`, obj)
            .then((res) => {
                const status =
                    res?.data?.data?.status === 'active'
                        ? en_ja['Active']
                        : en_ja['Disabled'];

                const message = `${en_ja['Deep link status updated to']} ${status}`;

                setSnackbarMessage(message);
                setSnackbarType('success');
                setSnackbarOpen(true);

                getAllDeepLinks();
            })
            .catch((err) => {
                console.error(err);

                setSnackbarMessage(en_ja['Could not update Status']);
                setSnackbarType('error');
                setSnackbarOpen(true);
            });
    };

    const handleShowQr = async (row) => {
        setQrDialogOpen(true);
        setQrLoading(true);
        setQrImage('');
        setQrTokenId(row.code || '');
        setQrDeepLinkUrl(row.deep_link_url || '');

        try {
            const res = await api.get(`/v1/admin/deep-links/${row.id}/qr`);

            if (res?.data?.success) {
                const data = res?.data?.data || {};
                setQrImage(data.qr_preview || data.qr_png || '');
                if (data.deep_link_url) setQrDeepLinkUrl(data.deep_link_url);
                if (data.code) setQrTokenId(data.code);
            }
        } catch (err) {
            console.error(err);
            setSnackbarMessage(en_ja['Failed to load QR Code'] || 'Failed to load QR Code');
            setSnackbarType('error');
            setSnackbarOpen(true);
        } finally {
            setQrLoading(false);
        }
    };

    const handleCloseQr = () => {
        setQrDialogOpen(false);
        setQrImage('');
        setQrTokenId('');
        setQrDeepLinkUrl('');

        getAllDeepLinks();
    };

    const handleCopyLink = async (row) => {
        try {
            await navigator.clipboard.writeText(row.deep_link_url);
            setSnackbarMessage(en_ja['Link copied to clipboard']);
            setSnackbarType('success');
            setSnackbarOpen(true);
        } catch (err) {
            setSnackbarMessage(en_ja['Failed to copy link']);
            setSnackbarType('error');
            setSnackbarOpen(true);
        }
    };

    const handleCopyQrLink = async () => {
        if (!qrDeepLinkUrl) return;
        try {
            await navigator.clipboard.writeText(qrDeepLinkUrl);
            setSnackbarMessage(en_ja['Link copied to clipboard']);
            setSnackbarType('success');
            setSnackbarOpen(true);
        } catch (err) {
            setSnackbarMessage(en_ja['Failed to copy link']);
            setSnackbarType('error');
            setSnackbarOpen(true);
        }
    };

    const triggerDownload = (href, filename) => {
        const a = document.createElement('a');
        a.href = href;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const handleDownloadPng = () => {
        if (!qrImage) {
            setSnackbarMessage(en_ja['QR not available'] || 'QR not available');
            setSnackbarType('error');
            setSnackbarOpen(true);
            return;
        }
        const filename = `qr_${qrTokenId || 'deep_link'}.png`;
        triggerDownload(qrImage, filename);
    };

    const formatDate = (value) => {
        if (!value) return '-';
        const d = new Date(value);
        if (isNaN(d.getTime())) return value;
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const columns = [
        { id: 'name', label: en_ja['Name'], sortable: true, minWidth: 200 },
        { id: 'code', label: en_ja['Token ID'], sortable: true, minWidth: 120 },
        {
            id: 'type',
            label: en_ja['Destination Type'],
            sortable: true,
            minWidth: 160,
            render: (row) => row?.type || en_ja["-"],
        },
        {
            id: 'link_kind',
            label: en_ja['QR Type'],
            sortable: true,
            minWidth: 160,
        },
        {
            id: 'status',
            label: en_ja['Status'],
            sortable: true,
            minWidth: 120,
            render: (row) => (
                <Typography color={row.status === "active" ? 'green' : 'red'}>
                    {row.status === "active" ? en_ja['Active'] : en_ja['Disabled']}
                </Typography>
            ),
        },
        {
            id: 'created_at',
            label: en_ja['Created Date'],
            sortable: true,
            minWidth: 140,
            render: (row) => (
                <Typography variant="body2" color="text.secondary">
                    {formatDate(row.created_at)}
                </Typography>
            ),
        },
        {
            id: 'Qr Scan Count',
            label: en_ja['Count'],
            sortable: true,
            minWidth: 100,
        },
        {
            id: 'actions',
            label: en_ja['Actions'],
            sortable: false,
            minWidth: 250,

            render: (row) => (
                <Box display="flex" justifyContent="start" gap={0.5}>
                    <Tooltip title={en_ja['Edit']} arrow>
                        <IconButton size="small" onClick={() => handleEdit(row)}
                            sx={{
                                "&:hover": {
                                    backgroundColor: "#eae6e6",
                                },
                            }}>
                            <EditIcon fontSize="small" sx={{ color: 'rgba(0,0,0,0.6)' }} />
                        </IconButton>
                    </Tooltip>

                    <Tooltip
                        title={
                            row.status === 'active'
                                ? en_ja['Active']
                                : en_ja['Disabled']
                        }
                        arrow
                    >
                        <IconButton
                            size="small"
                            onClick={() => handleToggleStatus(row)}
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "50%",
                                "&:hover": {
                                    backgroundColor:
                                        row.status === "active"
                                            ? "#d5f0d7"
                                            : "#e1e1e1",
                                },
                            }}
                        >
                            {row.status === "active" ? (
                                <ToggleOnIcon sx={{ color: "#3fa644" }} />
                            ) : (
                                <ToggleOffIcon sx={{ color: "#757575" }} />
                            )}
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={en_ja['Show QR Code']} arrow>
                        <IconButton size="small" onClick={() => handleShowQr(row)}
                            sx={{
                                color: "#1976d2",
                                "&:hover": {
                                    backgroundColor: "#e2ecf7",
                                },
                            }}>
                            <QrCodeIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={en_ja['Copy Link']} arrow>
                        <IconButton size="small" onClick={() => handleCopyLink(row)}
                            sx={{
                                color: '#e88e44',
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                "&:hover": {
                                    backgroundColor: "#f2e3d8",
                                },
                            }} >
                            <LinkIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={en_ja['Delete Link']} arrow>
                        <IconButton size="small" onClick={() => handleDeleteClick(row)} sx={{
                            color: "error.main",
                            "&:hover": { backgroundColor: "error.light", color: "error.dark", },
                            width: 32,
                            height: 32,
                        }}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                </Box>
            ),
        },
    ];

    const getAllDeepLinks = async () => {
        setLoading(true);
        try {
            const res = await api.get('/v1/admin/get/deep-links');
            if (res && res.data && res.data.data) {
                setAllDeepLinks(res.data.data);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getAllDeepLinks();

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                getAllDeepLinks();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    const handleDialog = () => {
        setDialogOpen(false);
        setEditMode(false);
        setSelectedDeepLink(null);
    };

    const handleAddDeepLink = () => {
        setEditMode(false);
        setSelectedDeepLink(null);
        setDialogOpen(true);
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
        setSnackbarMessage('');
        setSnackbarType('success');
    };

    return (
        <Box>
            <CustomSnackbar
                isOpen={snackbarOpen}
                type={snackbarType}
                message={snackbarMessage}
                onClose={handleSnackbarClose}
            />

            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                my: 3
            }}>
                <Box>
                    <SearchBar
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </Box>
                <Button sx={ButtonStyles} onClick={handleAddDeepLink}>
                    {en_ja['Add Deep Link']}
                </Button>
            </Box>

            <Card
                sx={{
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    overflow: 'hidden',
                }}
            >
                <CustomTable
                    columns={columns}
                    rows={allDeepLinks}
                    loading={loading}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    showIndexColumn={true}
                />
            </Card>

            {/* Add / Edit dialog */}
            <AddDeepLink
                open={dialogOpen}
                onClose={handleDialog}
                editMode={editMode}
                deepLinkData={selectedDeepLink}
                onSubmit={handleDialogSubmit}
                setSnackbarType={setSnackbarType}
                setSnackbarOpen={setSnackbarOpen}
                setSnackbarMessage={setSnackbarMessage}
                originalDeepLinkData={originalDeepLinkData}
            />

            {/* QR Code Dialog */}
            <Dialog
                open={qrDialogOpen}
                onClose={handleCloseQr}
                maxWidth={false}
                PaperProps={{
                    sx: {
                        width: 400,
                        overflow: 'hidden',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        borderBottom: '1px solid #E5E7EB',
                        py: 2,
                        px: 3,
                    }}
                >
                    <Box>
                        <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                            {en_ja['QR Code'] || 'QR Code'}
                        </Typography>
                        {qrTokenId && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                            >
                                {(en_ja['Token:'] || 'Token:')} {qrTokenId}
                            </Typography>
                        )}
                    </Box>

                    <IconButton onClick={handleCloseQr} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 3 }}>
                    {/* QR Preview */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: 280,
                            mb: 3,
                        }}
                    >
                        {qrLoading ? (
                            <CircularProgress />
                        ) : qrImage ? (
                            <img
                                src={qrImage}
                                alt="QR Code"
                                style={{
                                    width: '100%',
                                    maxWidth: '300px',
                                    objectFit: 'contain',
                                }}
                            />
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                {en_ja['QR not available'] || 'QR not available'}
                            </Typography>
                        )}
                    </Box>

                    {/* Deep Link URL */}
                    <Box sx={{ mb: 2.5 }}>
                        <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ mb: 1, color: '#212529' }}
                        >
                            {en_ja['Deep Link URL'] || 'Deep Link URL'}
                        </Typography>
                        <TextField
                            value={qrDeepLinkUrl}
                            fullWidth
                            size="small"
                            InputProps={{
                                readOnly: true,
                                sx: {
                                    fontFamily: 'monospace',
                                    fontSize: '0.85rem',
                                    backgroundColor: '#F8F9FA',
                                },
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip title={en_ja['Copy Link'] || 'Copy Link'} arrow>
                                            <IconButton
                                                size="small"
                                                onClick={handleCopyQrLink}
                                                disabled={!qrDeepLinkUrl}
                                            >
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    {/* Download button */}
                    <Button
                        variant="contained"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleDownloadPng}
                        disabled={!qrImage || qrLoading}
                        sx={{
                            width: '100%',
                            backgroundColor: '#dc3545',
                            color: '#fff',
                            textTransform: 'none',
                            fontWeight: 600,
                            py: 1.1,
                            borderRadius: '8px',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: '#c82333',
                                boxShadow: 'none',
                            }
                        }}
                    >
                        {en_ja['Download'] || 'Download'}
                    </Button>
                </DialogContent>
            </Dialog>

            <Dialog
                open={deleteConfirmOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle id="delete-dialog-title" sx={{ color: "error.main" }}>
                    {en_ja["Confirm Delete"]}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        {en_ja["Are you sure you want to delete this deep link"]}  <strong>{deepLinkToDelete?.name}</strong>? {en_ja["This action cannot be undone."]}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={handleDeleteCancel} variant="outlined" color="primary">
                        {en_ja["Cancel"]}
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        color="error"
                        autoFocus
                    >
                        {en_ja["Delete"]}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box >
    );
};

export default DeepLinkManagement;